import { departmentsApi } from "../../../../../../../../../services/accounting/department/api";
import { contactsApi } from "../../../../../../../../../services/accounting/contact/api";
import { productsApi } from "../../../../../../../../../services/accounting/product/api";
import { Product } from "../../../../../../../../../services/accounting/product/types";
import { Department } from "../../../../../../../../../services/accounting/department/types";
import { Contact } from "../../../../../../../../../services/accounting/contact/types";
import { Contract } from "../../../../../../../../../services/accounting/contract/types";
import { InvoiceDraftItem } from "../../../../../../../../../services/dataEntering/invoiceDraft/types";
import { AppDispatch, GetRootState } from "../../../../../../../../../shared/redux/types";

export interface InvoiceItemFormState {
    product: null | Product;
    productInferred: boolean;
    predictedProductId: string | null;
    predictionConfidence: number | null;
    quantity: number;
    vatRate: number;
    totalPrice: number;
    vatInclusive: boolean;
    contract: null | Contract;
    department: null | Department;
    description: string;
}

export const defaultInvoiceItem: InvoiceItemFormState = {
    product: null,
    productInferred: false,
    predictedProductId: null,
    predictionConfidence: null,
    quantity: 1,
    vatRate: 0,
    totalPrice: 0,
    vatInclusive: false,
    contract: null,
    department: null,
    description: "",
};

export const fetchContactById = async (
    contactId: string | null,
    accountId: number,
    dispatch: AppDispatch,
    getState: GetRootState,
): Promise<Contact | null> => {
    if (!contactId) {
        return null;
    }

    const { data } = await contactsApi.endpoints.getContact.initiate({ accountId, contactId })(
        dispatch,
        getState,
        null,
    );
    return data ?? null;
};

export const fetchDepartmentById = async (
    departmentId: string | null,
    accountId: number,
    dispatch: AppDispatch,
    getState: GetRootState,
): Promise<Department | null> => {
    if (!departmentId) {
        return null;
    }

    const departments = await departmentsApi.endpoints.getAllDepartments
        .initiate({ accountId })(dispatch, getState, null)
        .unwrap();
    return departments.find((d) => d.departmentId === departmentId) ?? null;
};

export const fetchProductById = async (
    productId: string,
    accountId: number,
    dispatch: AppDispatch,
    getState: GetRootState,
): Promise<Product | null> => {
    try {
        return await productsApi.endpoints.getProduct
            .initiate({ accountId, productId })(dispatch, getState, null)
            .unwrap();
    } catch {
        return null;
    }
};

export const transformDraftItems = async (
    draftItems: InvoiceDraftItem[],
    accountId: number,
    dispatch: AppDispatch,
    getState: GetRootState,
): Promise<InvoiceItemFormState[]> => {
    const transformed = await Promise.all(
        draftItems.map(async (item) => ({
            product: item.productId
                ? await fetchProductById(item.productId, accountId, dispatch, getState)
                : null,
            productInferred: item.productInferred,
            predictedProductId: item.predictedProductId,
            predictionConfidence: item.predictionConfidence,
            quantity: item.quantity,
            vatRate: item.vatRate,
            totalPrice: item.totalPrice,
            vatInclusive: false,
            contract: null,
            department: null,
            description: item.description,
        })),
    );

    return transformed.length > 0 ? transformed : [defaultInvoiceItem];
};
